import React, { useState, useEffect } from 'react';
import {
	Modal, ModalContent, ModalHeader, ModalBody, ModalFooter,
	Button, RadioGroup, Radio
} from '@heroui/react';

/**
 * Edit recurring scope options — 3 entries (legacy parity).
 *   - `current`      Just this event.
 *   - `not-started`  Not yet started events (future + currently-not-started).
 *   - `following`    This and all following.
 */
export const EDIT_RECURRING_OPTIONS = [
	{ value: 'current', label: 'Just this event' },
	{ value: 'not-started', label: 'Not yet started events' },
	{ value: 'following', label: 'This and all following events' }
];

/**
 * Delete recurring scope options — 4 entries (Edit options + `all`).
 */
export const DELETE_RECURRING_OPTIONS = [
	{ value: 'current', label: 'Just this event' },
	{ value: 'not-started', label: 'Not yet started events' },
	{ value: 'following', label: 'This and all following events' },
	{ value: 'all', label: 'All events in series' }
];

/**
 * RecurringScopeConfirm — small HeroUI modal that asks the user which slice
 * of a recurring series an action should apply to.
 *
 * Used twice in the Edit/Delete flow:
 *   - Save a recurring event (`actionLabel="Save"`, `EDIT_RECURRING_OPTIONS`).
 *   - Delete a recurring event (`actionLabel="Delete"`, `DELETE_RECURRING_OPTIONS`).
 *
 * Default selection is the first option (`current`). The dialog does NOT
 * stack — caller opens it AFTER closing the source EventViewModal/edit form
 * (or while the source modal is hidden), per HeroUI single-modal contract.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {(scope: string) => void} props.onConfirm
 * @param {'Save'|'Delete'|string} props.actionLabel
 * @param {Array<{value:string,label:string}>} props.options
 * @param {string} [props.eventTitle] Optional — surfaced in the body for context.
 */
export default function RecurringScopeConfirm({
	isOpen,
	onClose,
	onConfirm,
	actionLabel,
	options,
	eventTitle
}) {
	const safeOptions = Array.isArray(options) && options.length > 0
		? options
		: EDIT_RECURRING_OPTIONS;
	const defaultValue = safeOptions[0].value;
	const [scope, setScope] = useState(defaultValue);

	// Reset the selection every time the dialog reopens so a previous action's
	// choice doesn't leak into a new one.
	useEffect(() => {
		if (isOpen) setScope(defaultValue);
	}, [isOpen, defaultValue]);

	const isDestructive = String(actionLabel).toLowerCase() === 'delete';

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="md" placement="center">
			<ModalContent>
				<ModalHeader>
					<span className="text-lg font-bold">
						{actionLabel} recurring event
					</span>
				</ModalHeader>
				<ModalBody className="pt-2">
					{eventTitle && (
						<p className="text-sm text-default-600 mb-2">
							{eventTitle}
						</p>
					)}
					<p className="text-sm text-default-500 mb-3">
						Choose which occurrences this {actionLabel.toLowerCase()} should affect:
					</p>
					<RadioGroup
						aria-label={`${actionLabel} scope`}
						value={scope}
						onValueChange={setScope}
					>
						{safeOptions.map(opt => (
							<Radio key={opt.value} value={opt.value}>
								{opt.label}
							</Radio>
						))}
					</RadioGroup>
				</ModalBody>
				<ModalFooter>
					<Button variant="light" onPress={onClose}>
						Cancel
					</Button>
					<Button
						color={isDestructive ? 'danger' : 'primary'}
						onPress={() => onConfirm?.(scope)}
					>
						{actionLabel}
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
}
